import React, { useEffect, useState, useMemo, useCallback, useImperativeHandle, forwardRef } from "react";
import { stableSort, getSorting, Order } from "./TableComponents/Helperfunctions";
// Table Components
import { TableControls } from "./TableComponents/TableControls";
import { TableHeader, ITableColumn } from "./TableComponents/TableHeader";
import { TableCellContent } from "./TableComponents/TableCellContent";
// Material UI
import LinearProgress from "@mui/material/LinearProgress";
import Checkbox from "@mui/material/Checkbox";
import { TableContainer, Table, TableBody, TableCell, TableRow, Grid, styled } from "@mui/material";
import Typography from "@mui/material/Typography";

// Components
import { CheckboxField } from "src/components/FormikComponents/CheckboxField";
import { TablePagination } from "./TablePagination";

import { ISearchStatus, defaultSearchStatus } from "src/components/SearchFilters/Helpers";

// Styling
import classNames from "classnames";

import { EventModalTypes } from "src/components/DialogModal/DialogModalWrapper";
import { setIsFlightContextOpen } from "src/redux/actions/flights/FlightTimeline.Actions";
import { useDispatch } from "react-redux";
import { v1 } from "uuid";
import { SwitchField } from "../FormikComponents/SwitchField";

// Imports for new load results into pairing waterfall button
import { IconButton } from "../IconButton/IconButton";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { updateWaterfallPairings } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { openWindow } from "src/redux/actions/Mosaic.Actions";
import { LoadingMessage } from "../LoadingMessage/LoadingMessage";
import { convertSearchPairingToDisplayPairing, ISearchPairing } from "@vmosolutions/oc-interfaces";

interface IFilterSearchTable {
  id: string;
  path?: string[];
  events?: any;
  className?: string;
  parentId?: string;
  uniqueKey?: string;
  orderDirection?: Order;
  hideFilter?: boolean;
  hideFocusSelected?: boolean;
  hideShowColumns?: boolean;
  selectModeSingle?: boolean;
  defaultSelectedRows?: any[];
  changeSelectedRows?: (rows: any, focusSelected?: boolean, dataType?: string) => void;
  selectedRowIndex?: number;
  allowMultiFilter?: boolean;
  actions?: any;
  showEventActions?: boolean;
  setActiveEventModal?: (activeEventModal: EventModalTypes) => void;
  hideSelectionCheckboxes?: boolean;
  defaultRowsPerPage?: number;
  disableRowSelect?: boolean;
  stickyHeader?: boolean;
  maxTableHeight?: number;
  maxTableWidth?: number;
  showDateRangeRRule?: boolean;
  optionalControls?: JSX.Element;
  selectUsingCheckboxesOnly?: boolean;
  showUTCLocalToggle?: boolean;
  showSavedFilters?: boolean;
  defaultOrderKey?: string; // Table will use this key to override default sort key.
  showAsUTCTime?: boolean;
}

/** Check if uniqueIds exists, if not assign them */
const assignUniqueIds = (uniqueId: string, data: any[]): any[] => {
  return data.map((row: any) => {
    if (row?.[uniqueId]) {
      return row;
    } else {
      return { ...row, [uniqueId]: v1() };
    }
  });
};

const OCFilterSearchTable = styled("div")(({ theme }) => ({
  overflowX: "auto",
  height: "100%",
  backgroundColor: theme.custom.tables.backgroundColor,

  "& .table_info": {
    margin: 12,
  },

  "& .focus_selections": {
    margin: "5px 14px",
    width: "auto",
    alignItems: "baseline",
    "& .label": { margin: "auto auto auto 5px" },
  },

  "& .time_mode_selector": {
    margin: "10px 0px",
    width: "auto",
    justifyContent: "unset",
    "& label": {
      width: "auto",
      color: theme.palette.primary.contrastText,
      fontSize: 12,
      fontWeight: 300,
      margin: "0px 10px",
    },

    "&.focused label": { color: "unset" },
    "& .switch-buttons": {
      margin: "-4px 4px -4px 0px",
      "& button": { height: 14 },
    },

    "&.active": {
      "& .switch-buttons button.disabled": {
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.background.default,
      },
    },
  },

  "& .load_into_pairing_waterfall_btn": {
    fontSize: 12,
    margin: "5px 14px",
    "& button": {
      padding: 0,
      marginRight: 5,
    },
  },

  "& .table_controls": {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    marginLeft: 4,
    "& .control_icon": { marginRight: "5px" },
    "& .table_eventtype_control": {
      minWidth: 295,
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      margin: "5px 10px",
      alignItems: "flex-start",
    },
    "& .table_filter_control": {
      minWidth: 295,
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "flex-start",
    },
    "& .table_columnview_control": {
      minWidth: 295,
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      margin: "5px 10px",
      alignItems: "flex-start",
      "& .show_hide_selection": { margin: 0, width: 263 },
    },
    "& .table_filter_selection": { margin: "0px 0px 5px 0px" },
    "& .table_event_selection, & .timeline_event_search_filter": {
      margin: 0,
      "& .input_control .react-select-container": { width: 263 },
    },

    // Table filters styles (overrides certain styles assigned by the form components).
    "& .selection_filters_container": {
      display: "flex",
      flexDirection: "column",
      "& .table_filter_textfield": {
        margin: "0px 0px 5px 0px",
        "& .input_control > label": {
          width: 90,
          textTransform: "capitalize",
        },
        "& .input_control > div.input-base": { width: 170 },
      },
      "& .table_filter_datetime": {
        "& .from_date, & .to_date": {
          margin: "0px 0px 5px 0px",
          "& .input_control > label": { width: 90 },
        },
      },
    },
  },

  "& .focus_on_select": {
    display: "flex",
    flexWrap: "wrap",
    margin: "0px 10px",
    userSelect: "none",
    "& .checkbox_selection": {
      "& .label": { width: "auto" },
    },
    "&.disabled": {
      opacity: 0.5,
      pointerEvents: "none",
    },
  },

  "& .table_content": {
    "& .MuiTableCell-root": {
      padding: "0px 5px",
      borderColor: theme.palette.background.default,
      "&.MuiTableCell-head": {
        lineHeight: "1rem",
        padding: 5,
      },
    },
    "& thead": {},
    "& .table_row": {
      "&:nth-of-type(odd)": {
        backgroundColor: theme.custom.tables.oddRowColor,
        "&:hover": {
          backgroundColor: theme.custom.tables.oddRowHoverColor,
        },
      },
      "&:nth-of-type(even)": {
        backgroundColor: theme.custom.tables.evenRowColor,
        "&:hover": {
          backgroundColor: theme.custom.tables.evenRowHoverColor,
        },
      },
      "& .table-cells-dates": {},
      "& .MuiTableCell-root": {
        fontSize: 12,
        fontWeight: 300,
        color: theme.custom.tables.contentColor,
      },
      "& .no_data": { padding: 10 },
    },
  },
  "& .table_pagination": { minWidth: 295 },
  "& .table_actions": {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    margin: "6px 12px",
    "& .checkbox_selection": {
      "& .label": { width: "auto" },
    },
    "&.disabled": {
      opacity: 0.5,
      userSelect: "none",
      pointerEvents: "none",
    },
    "& .control_actions_icon": { marginRight: 5 },
    "& .table_actions_selection": { margin: 0 },
  },
}));

/////////////////////////////////////////////////////
///////////  FILTER SEARCH TABLE FUNCTION
/////////////////////////////////////////////////////
/**
 * Reusable component for a search by filter table with the ability to sort the data
 * @param props -
 */
export const FilterSearchTable = forwardRef((props: IFilterSearchTable, ref: any): JSX.Element => {
  const dispatch = useDispatch();
  const {
    id,
    className,
    selectModeSingle,
    changeSelectedRows,
    selectedRowIndex,
    defaultSelectedRows,
    hideFilter,
    hideFocusSelected,
    hideShowColumns,
    events,
    parentId,
    uniqueKey,
    orderDirection,
    allowMultiFilter,
    hideSelectionCheckboxes,
    defaultRowsPerPage,
    disableRowSelect,
    stickyHeader,
    maxTableHeight,
    maxTableWidth,
    showDateRangeRRule,
    optionalControls,
    selectUsingCheckboxesOnly,
    showUTCLocalToggle,
    showSavedFilters,
    defaultOrderKey,
    showAsUTCTime,
  } = props; // showEventActions, setActiveEventModal

  // Default selected Event (data) Type
  const parentElement: HTMLElement = useMemo(() => !!parentId && document.getElementById(parentId), [parentId]);
  const [selectedEvent, setSelectedEvent] = useState<any>(events[0]);
  const { tableInfo, dataType } = selectedEvent; //data

  const [searchStatus, setSearchStatus] = useState<ISearchStatus>(defaultSearchStatus);

  // Setup detaul Selected Rows
  const [selectedRows, setSelectedRows] = useState<any[]>(defaultSelectedRows ? defaultSelectedRows : []);
  const [focusSelected, setFocusSelected] = useState<boolean>(true);
  // Anything added here will be available to call via the parent component using ref.
  useImperativeHandle(ref, () => ({
    clearSelectedrows() {
      setSelectedRows([]);
    },
  }));

  // Page and Column Ordering
  const [page, setPage] = useState<number>(0);
  const [order, setOrder] = useState<Order>(orderDirection ? orderDirection : "desc");
  const [orderBy, setOrderBy] = useState<string>(defaultOrderKey ?? uniqueKey);
  const [rowsPerPage, setRowsPerPage] = useState<number>(defaultRowsPerPage ? defaultRowsPerPage : 5);
  const [displayAsUTC, setDisplayAsUTC] = useState<boolean>(false);

  useEffect(() => {
    if (showAsUTCTime !== undefined) setDisplayAsUTC(showAsUTCTime);
  }, [showAsUTCTime]);

  // Setup Visible Columns
  const [selectedColumnDisplay, setSelectedColumnDisplay] = useState<string[]>(
    !!tableInfo && tableInfo.filter((heading: any) => heading.visible)
  );
  const [tableHeadings, setTableHeadings] = useState<ITableColumn[]>(
    tableInfo?.filter((heading: any) => heading.value && heading.visible)
  );

  // Deprecated useState - I'm not certain why we were using this as it was just causing an uneeded re-render. - CC
  // const [selectedEventData, setSelectedEventData] = useState(null);

  // Stored filtered and Sorted Data
  const [filteredData, setFilteredData] = useState<any>([]);

  /**
   *  Convert pairings found to IDisplayPairing format then send to pairing waterfall reducer.
   *  Then open Pairing Waterfall or focus existing if already open.
   */
  const [loadingPairingWaterfall, setLoadingPairingWaterfall] = useState<boolean>(false);
  const openPairingWaterfallWindow = useCallback(() => {
    if (dataType !== "pairings" || !dispatch || !filteredData?.length) {
      return;
    }
    setLoadingPairingWaterfall(true);
    dispatch(openWindow("Pairing Waterfall", "PairingWaterfallWindow", [], {}, "content", true));

    const displayPairings = filteredData.map((pairing: ISearchPairing) =>
      convertSearchPairingToDisplayPairing(pairing)
    );
    if (Array.isArray(displayPairings) && displayPairings?.length) {
      const sortedDisplayPairings = displayPairings.sort((a, b) => a.start - b.start || a.end - b.end);
      dispatch(updateWaterfallPairings(sortedDisplayPairings));
      setLoadingPairingWaterfall(false);
    }
  }, [dispatch, filteredData, dataType]);

  // If table info is modified, update the table headings accordingly.
  useEffect(() => setTableHeadings(tableInfo?.filter((heading: any) => heading.value && heading.visible)), [tableInfo]);

  // If event data changes or component is mounted, selectedrow index if passed or clear selection.
  useEffect(() => {
    const preSelectRow =
      selectedRowIndex !== null && selectedRowIndex > -1 && events[0]?.data ? [events[0].data[selectedRowIndex]] : [];
    setSelectedRows(preSelectRow);
  }, [events, selectedRowIndex]);

  useEffect(() => {
    if (events) {
      let found: any = null;
      // find the selected event in the props.events
      found = events.find((event: any) => {
        return event.id === selectedEvent.id;
      });
      if (!!found && !!found.data) {
        setSelectedEvent(found);
        // Check the Data set to ensure that the assigned uniqueKey field exists, if not, add it (sorting breaks without it)
        const tableData = uniqueKey ? assignUniqueIds(uniqueKey, found.data) : found.data;
        // setSelectedEventData(tableData);
        // Apply Default Sort to filteredData
        setFilteredData(stableSort(tableData, getSorting(orderDirection ?? "desc", uniqueKey)));
      }
    }
  }, [events, selectedEvent, uniqueKey, orderDirection]);

  //Make sure that if are searching the table and you aren't on page 1
  //you get directed back to page 1 to see your results
  useEffect(() => {
    if (filteredData.length > 0) handleChangePage(null, 0, 5);
  }, [filteredData]);

  // This is triggered whenever order or ordereBy are modified by clicking columns in the table to sort.
  useEffect(() => {
    setFilteredData((prev: any[]) => {
      const sortedData = stableSort(prev, getSorting(order, orderBy));
      return sortedData;
    });
  }, [order, orderBy]);

  // Functions that handle the sorting in the tables (this is from the Material UI example)
  //---------------------------------------------------------------------------------------------------------------------------------------------------
  const handleRequestSort = useCallback((event: React.MouseEvent<unknown>, property: any): void => {
    setOrder((prev) => (prev === "desc" ? "asc" : "desc"));
    setOrderBy(property);
  }, []);

  // Functions for multiselecting rows (modified to ensure it only selects from filteredData) -- CC
  //---------------------------------------------------------------------------------------------------------------------------------------------------
  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (!event.target.checked || (selectedRows.length > 0 && filteredData.length === selectedRows.length)) {
      setSelectedRows([]);
      if (changeSelectedRows) changeSelectedRows([], focusSelected, dataType);
      return;
    } else {
      const newSelecteds = filteredData.map((n: any, index: number) => {
        return n[props.uniqueKey] ? n : index;
      });
      setSelectedRows(newSelecteds);
      if (changeSelectedRows) changeSelectedRows(newSelecteds, focusSelected, dataType);
      return;
    }
  };

  /**
   * Handles the rows that are selected with check boxes
   * @param event -
   * @param item -
   */
  const handleClick = useCallback(
    (event: React.MouseEvent<unknown>, item: any): void => {
      const selectedIndex = selectedRows.indexOf(item);
      let newSelected: string[] = [];
      // case where you only want a single row to be selected at a time
      if (props.selectModeSingle) {
        // case where there are no selected rows
        if (selectedIndex === -1 && selectedRows.length === 0) {
          newSelected = newSelected.concat(selectedRows, item);
        }
        // if you selected a row that is the selected row already then deselect
        else if (selectedIndex === 0) {
          newSelected = [];
        }
        // if you select a row that is different than the one you have currently selected
        else {
          newSelected = newSelected.concat([], item);
        }
      }
      //case where you want to multi select
      else {
        // Appends first item
        if (selectedIndex === -1) {
          newSelected = newSelected.concat(selectedRows, item);
        }
        // removes first item
        else if (selectedIndex === 0) {
          newSelected = newSelected.concat(selectedRows.slice(1));
        }
        //removes last item
        else if (selectedIndex === selectedRows.length - 1) {
          newSelected = newSelected.concat(selectedRows.slice(0, -1));
        }
        // removes item in the middle
        else if (selectedIndex > 0) {
          newSelected = newSelected.concat(selectedRows.slice(0, selectedIndex), selectedRows.slice(selectedIndex + 1));
        }
      }
      // Update state and fire off function to update parent if available.
      setSelectedRows(newSelected);
      if (changeSelectedRows) changeSelectedRows(newSelected, focusSelected, dataType);
    },
    [changeSelectedRows, dataType, focusSelected, props.selectModeSingle, selectedRows]
  );

  const isSelected = useCallback(
    (rowKey: string): boolean => {
      return rowKey && selectedRows?.length > 0 ? selectedRows.includes(rowKey) : false;
    },
    [selectedRows]
  );

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
    rowsPerPage: number
  ): void => {
    if (rowsPerPage !== null) {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (value: number): void => {
    if (value) {
      const newPageIndex: number = Math.trunc((page * (rowsPerPage + 1)) / value);
      setPage(newPageIndex);
    } else {
      setPage(0);
    }
    setRowsPerPage(value);
  };

  const handleRowContext = useCallback(
    (mouseEvent: React.MouseEvent): boolean => {
      if (disableRowSelect) {
        return false;
      }
      mouseEvent.preventDefault();
      if (mouseEvent.button === 2) {
        dispatch(setIsFlightContextOpen(true, id));
      }
      return false;
    },
    [disableRowSelect, dispatch, id]
  );

  const renderFilteredData: JSX.Element | JSX.Element[] = useMemo(() => {
    const data = filteredData;
    const { tableInfoDisplay, tableInfo } = selectedEvent;
    const { noMessage } = tableInfoDisplay;
    if (!!data && data.length > 0) {
      return data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row: any, index: number) => {
        const labelId = `table-checkbox-${index}`;
        const rowKey = row[props.uniqueKey] || props.uniqueKey ? row : page * rowsPerPage + index;
        const isItemSelected = isSelected(rowKey);
        return (
          <TableRow
            aria-checked={isItemSelected}
            className="table_row"
            hover
            key={index}
            // disable this functionality if selectUsingCheckboxesOnly is set true. - CC
            onClick={(event) => !disableRowSelect && !selectUsingCheckboxesOnly && handleClick(event, rowKey)}
            onContextMenu={handleRowContext}
            tabIndex={-1}
            role="checkbox"
            selected={isItemSelected}
          >
            {!hideSelectionCheckboxes ? (
              <TableCell padding="checkbox">
                <Checkbox
                  checked={isItemSelected}
                  inputProps={{ "aria-labelledby": labelId }}
                  onClick={(event) => !disableRowSelect && handleClick(event, rowKey)}
                />
              </TableCell>
            ) : null}

            <TableCellContent
              row={row}
              rowIndex={index}
              tableHeadings={tableHeadings}
              dataType={dataType}
              isSelected={isItemSelected}
              id={id}
              displayAsLocal={!displayAsUTC}
            />
          </TableRow>
        );
      });
    }
    return (
      <TableRow className="table_row">
        <TableCell colSpan={tableInfo.length + 1}>
          {/* +1 is added here to account for checkbox column */}
          <Typography variant="body1" className="no_data">
            {searchStatus?.message ? searchStatus.message : noMessage}
          </Typography>
        </TableCell>
      </TableRow>
    );
  }, [
    dataType,
    disableRowSelect,
    displayAsUTC,
    filteredData,
    handleClick,
    handleRowContext,
    hideSelectionCheckboxes,
    id,
    isSelected,
    page,
    props.uniqueKey,
    rowsPerPage,
    searchStatus.message,
    selectUsingCheckboxesOnly,
    selectedEvent,
    tableHeadings,
  ]);

  const renderTableInfo = (): JSX.Element => {
    const { tableInfoDisplay } = selectedEvent;
    const { tableTitle, tableDescription } = tableInfoDisplay;
    if (!tableTitle && !tableDescription) {
      return null;
    }
    return (
      <div className="table_info">
        {tableTitle && (
          <Typography variant="h6" id="tableTitle">
            {tableTitle}
          </Typography>
        )}
        {tableDescription && <Typography variant="subtitle1">{tableDescription}</Typography>}
      </div>
    );
  };

  /**
   * Accepts a type of event selected from the event drop down menu
   * @param selectedEvent -
   */
  const handleSelectedEvent = (selectedEvent: any): void => {
    //I think when I change event types I should clear the data (ie go from flights -> pairings)
    setFilteredData([]);
    if (selectedEvent) {
      setSelectedEvent(selectedEvent);
      const defaultVisibleColumns: any[] = [];
      selectedEvent.tableInfo.forEach((item: any) => {
        if (item.visible) defaultVisibleColumns.push(item);
      });
      setSelectedColumnDisplay(defaultVisibleColumns);
      // Update the table headings when switching between events.
      const visibleColumnValues = defaultVisibleColumns.map((column: any) => {
        return column.value;
      });
      const tableHeadings = selectedEvent.tableInfo.filter((heading: any) =>
        visibleColumnValues.includes(heading.value)
      );
      setTableHeadings(tableHeadings);
      setPage(0);
    } else {
      setSelectedEvent({
        tableInfo: [],
        tableInfoDisplay: {
          noMessage: "",
          tableDescription: "Use the drop down to select an event type.",
          tableTitle: "No Selection",
        },
      });
      setTableHeadings([]);
      setPage(0);
    }
  };

  /**
   * Will hide/show columns from the table when users select/deslect the checkboxed items.
   * Will also remove filters of columns that aren't visible. - CC
   * @param selectedColumnDisplayed -
   */
  const handleSelectedColumnDisplay = (selectedColumnDisplayed: any): void => {
    // Update appearance of the select menu checklist.
    setSelectedColumnDisplay(selectedColumnDisplayed);
    // The drop down menu will update all the available filters given by the selected columns currently displayed in the table
    const filterValues: any[] = [];
    selectedEvent.tableInfo.forEach((item: any) => {
      if (item.filter) {
        filterValues.push(item.value);
      }
    });
    const currentFiltersAvailableInOrder: any[] = [];
    filterValues.forEach((value: any) => {
      selectedColumnDisplayed.forEach((column: any) => {
        if (value === column.value) {
          currentFiltersAvailableInOrder.push(column);
        }
      });
    });
    // Create simple string array that we can use to easily filter other dependent arrays.
    const visibleColumnValues = selectedColumnDisplayed.map((column: any) => {
      return column.value;
    });
    // Remove any table headings that don't appear in the visible columns;
    const newTableHeadings = selectedEvent.tableInfo.filter((column: any) =>
      visibleColumnValues.includes(column.value)
    );
    setTableHeadings(newTableHeadings);
  };

  // Set filtered data.
  const handleFilteredEvents = useCallback(
    (data: any) => {
      const newFilteredData = stableSort(data, getSorting(order, orderBy));
      if (JSON.stringify(newFilteredData) !== JSON.stringify(filteredData)) {
        setFilteredData(newFilteredData);
      }
    },
    [filteredData, order, orderBy]
  );

  return (
    <OCFilterSearchTable className={classNames(className)}>
      {renderTableInfo()}

      <TableControls
        events={events}
        selectedEvent={selectedEvent}
        onSelectEvent={handleSelectedEvent}
        hideFilter={hideFilter}
        hideShowColumns={hideShowColumns}
        //dataset={flights}
        onFilteredEvents={handleFilteredEvents}
        columns={tableInfo}
        selectedColumns={selectedColumnDisplay}
        onSelectColumns={handleSelectedColumnDisplay}
        parentElement={parentElement}
        page={page}
        changePage={(page: number) => setPage(page)}
        allowMultiFilter={allowMultiFilter}
        searchStatus={(status: ISearchStatus) => setSearchStatus(status)}
        showDateRangeRRule={showDateRangeRRule}
        optionalControls={optionalControls}
        showSavedFilters={showSavedFilters}
      />

      {!hideFocusSelected && (
        <CheckboxField
          className="focus_selections"
          name="focus_selections"
          value={focusSelected}
          onChange={() => setFocusSelected(!focusSelected)}
          label={
            dataType === "flights"
              ? "Focus selected flights in timeline"
              : dataType === "groundEvents"
                ? "Focus selected ground events in timeline"
                : dataType === "pairings"
                  ? "Focus selected pairings in crew schedule"
                  : "Focus selected items"
          }
          labelPlacement="end"
          hideFocus={true}
        />
      )}

      {dataType === "pairings" && (
        <Grid container wrap="nowrap" className="load_into_pairing_waterfall_btn" alignItems="center">
          <IconButton
            color="primary"
            onClick={() => openPairingWaterfallWindow()}
            disabled={!filteredData?.length}
            size="large"
          >
            <OpenInNewIcon />
          </IconButton>
          <LoadingMessage
            message={
              loadingPairingWaterfall
                ? "Loading results into pairing waterfall"
                : "Display results in pairing waterfall window"
            }
            animate={loadingPairingWaterfall}
          />
        </Grid>
      )}

      {/* DISPLAY DATE/TIME LOCAL UTC SWITCH */}
      {showUTCLocalToggle && (
        <SwitchField
          className={`time_mode_selector ${!displayAsUTC && "active"}`}
          name={"time_mode_selector"}
          value={displayAsUTC}
          onChange={() => setDisplayAsUTC(!displayAsUTC)}
          switchlabels={["UTC", "LOCAL"]}
          switchtooltips={["Times as UTC", "Times as local"]}
          label="Display Date/Times in"
          allowNull={false}
        />
      )}

      <TableContainer
        className="table_container"
        style={{
          maxHeight: maxTableHeight ? maxTableHeight : "auto",
          maxWidth: maxTableWidth ? maxTableWidth : "auto",
        }}
      >
        <Table className="table_content" stickyHeader={stickyHeader}>
          <TableHeader
            numSelected={selectedRows.length}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            onSelectAllClick={handleSelectAllClick}
            rowCount={filteredData?.length ?? 0}
            rowHeadings={tableHeadings}
            selectModeSingle={selectModeSingle}
            hideSelectionCheckboxes={hideSelectionCheckboxes}
          />
          <TableBody>
            <TableRow className="table_row">
              <TableCell colSpan={tableInfo.length + 1}>
                {searchStatus?.active && <LinearProgress style={{ margin: "0px -4px" }} />}
              </TableCell>
            </TableRow>
            {renderFilteredData}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        mosaicId={id}
        className="table_pagination"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        parentElement={parentElement}
        lastSearched={searchStatus?.lastSearched}
      />
    </OCFilterSearchTable>
  );
});
